import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
} from '@material-ui/core';
import { t } from 'ttag';
import { useCreateBacnetUnit } from '@hooks/useBacnetDevicesApi';
import useStyles from './styles';

/**
 * Dialog for creating a new BACnet unit under a device.
 *
 * API signature: createBacnetUnit(bacnetDeviceId, { name, title?, description?, paramTemplate?, uiTemplate? })
 * - name: required, non-empty string
 * - title: optional display name for the unit
 * - description: optional
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Close handler (called with created unit or null)
 * @param {string} props.bacnetDeviceId - The parent BACnet device ID
 * @param {string} props.deviceName - The device name, shown in the dialog title
 */
const AddUnitDialog = ({ open, onClose, bacnetDeviceId, deviceName }) => {
  const classes = useStyles();
  const { mutate: createUnit, isLoading, error: apiError } = useCreateBacnetUnit();

  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [touched, setTouched] = useState({});

  const nameError = touched.name && !name.trim() ? t`Unit Name is required` : '';

  const handleCreate = async () => {
    setTouched({ name: true });
    if (!name.trim()) {
      return;
    }

    const payload = {
      name: name.trim(),
    };
    if (title.trim()) {
      payload.title = title.trim();
    }
    if (description.trim()) {
      payload.description = description.trim();
    }

    const result = await createUnit({ bacnetDeviceId, payload });
    if (result) {
      onClose(result);
    }
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    onClose(null);
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle className={classes.dialogTitle}>
        {t`Add App Unit`}
        {deviceName && (
          <span className={classes.deviceNameSubtitle}>{deviceName}</span>
        )}
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <TextField
          autoFocus
          required
          fullWidth
          label={t`Unit Name`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => setTouched((prev) => ({ ...prev, name: true }))}
          error={!!nameError}
          helperText={nameError}
          variant="outlined"
          size="small"
          className={classes.field}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && name.trim()) {
              handleCreate();
            }
          }}
        />

        {apiError && (
          <div className={classes.apiError}>
            {apiError.message || t`Failed to create unit`}
          </div>
        )}
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button onClick={handleCancel} disabled={isLoading} onMouseDown={(e) => e.preventDefault()}>
          {t`Cancel`}
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          color="primary"
          disabled={!name.trim() || isLoading}
        >
          {isLoading ? <CircularProgress size={20} /> : t`Create`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddUnitDialog;
